import { useState } from "react";
import {
  Box,
  Stack,
  Collapse,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
} from "@mui/material";
import {
  ExpandMore as ExpandIcon,
  Send as SendIcon,
} from "@mui/icons-material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import type { JobResponse } from "@taxassist/api-client";
import { UploadedFilesList } from "./UploadedFilesList";
import type { FileItem, BucketType, UploadedFileType } from "./fileList";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import { SubmitJobButton } from "./SubmitJobButton";
import { getStatusDisplay } from "../../utils/statusMapping";
import { downloadAllSourceFiles } from "../../utils/filePreviewUtils";
import { useNotification } from "../../hooks/useNotification";

const TERMINAL_STATUSES = ["DONE", "CANCELLED", "ARCHIVED"];

interface JobFilesSectionProps {
  job: JobResponse;
  uploadedFileItems: FileItem[];
  onUploadClick: () => void;
  onFileDownload: (file: FileItem) => Promise<void>;
  onFilePreview: (file: FileItem, index: number, files: FileItem[]) => void;
  onFileDelete: (file: FileItem) => Promise<void>;
  onUpdateFileType?: (
    file: FileItem,
    newType: UploadedFileType,
  ) => Promise<void>;
  onUpdateBucket?: (file: FileItem, newBucket: BucketType) => Promise<void>;
  context: "client" | "ops";
  onSubmitSuccess?: (job: JobResponse) => void;
  onSubmitError?: (error: Error, job: JobResponse) => void;
  onStartProcessing?: () => void;
  isStartProcessingPending?: boolean;
}

export function JobFilesSection({
  job,
  uploadedFileItems,
  onUploadClick,
  onFileDownload,
  onFilePreview,
  onFileDelete,
  onUpdateFileType,
  onUpdateBucket,
  context,
  onSubmitSuccess,
  onSubmitError,
  onStartProcessing,
  isStartProcessingPending,
}: JobFilesSectionProps) {
  const getInitialOpenState = () => {
    if (context !== "client") return true;

    const simplifiedStatus = getStatusDisplay(job.status);

    if (
      simplifiedStatus === "Unsubmitted" ||
      simplifiedStatus === "New Files"
    ) {
      return true;
    }

    if (simplifiedStatus === "Queued" || simplifiedStatus === "In Progress") {
      return false;
    }

    if (simplifiedStatus === "Done") {
      return false;
    }

    return true;
  };

  const [open, setOpen] = useState(getInitialOpenState());
  const [downloadingAll, setDownloadingAll] = useState(false);
  const notification = useNotification();

  const handleToggle = () => {
    setOpen(!open);
  };

  // Show download all button in ops context for all states before DONE
  const showDownloadAll =
    context === "ops" &&
    !TERMINAL_STATUSES.includes(job.status) &&
    (job.uploadedFiles?.length ?? 0) > 0;

  const handleDownloadAll = async () => {
    const jobId = job.jobId;
    if (!jobId) {
      notification.showError("Cannot download files: job ID is missing");
      return;
    }

    setDownloadingAll(true);
    try {
      const { downloadUrl, fileName } = await downloadAllSourceFiles(jobId);

      const url = new URL(downloadUrl, window.location.origin);
      if (url.protocol !== "https:") {
        throw new Error("Invalid download URL protocol");
      }

      const anchor = document.createElement("a");
      anchor.style.display = "none";
      anchor.href = url.toString();
      anchor.download = fileName;

      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);

      notification.showSuccess("Downloading all source files...");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Failed to download source files";
      notification.showError(errorMessage);
    } finally {
      setDownloadingAll(false);
    }
  };

  // For ops context, sort files by most recent first (no grouping)
  const displayFiles =
    context === "ops"
      ? [...uploadedFileItems].sort(
        (a, b) =>
          new Date(b.uploadedAt).getTime() - new Date(a.uploadedAt).getTime(),
      )
      : uploadedFileItems;

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: "#FFFFFF",
        borderRadius: 2,
        border: 1,
        borderColor: "#E7E7E7",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Stack direction="row">
          <Box>
            <IconButton
              aria-label="collapse"
              onClick={handleToggle}
              size="small"
              sx={{ transform: open ? "rotate(0)" : "rotate(-90deg)" }}
            >
              <ExpandIcon />
            </IconButton>
          </Box>
          <Stack gap={1} direction="row">
            <Typography variant="h6" sx={{ color: "text.primary" }}>
              {context === "ops" && <>1. Review Files</>}
              {context === "client" && <>1. Upload Files</>}
            </Typography>
            <Box>
              <Chip
                size="small"
                sx={{ color: "#757575", fontWeight: "bold" }}
                label={job.uploadedFiles?.length || 0}
              />
            </Box>
          </Stack>
        </Stack>
        {context === "client" && (
          <Stack direction="row" spacing={1}>
            <Button
              variant="text"
              sx={{ px: 2, fontWeight: 700 }}
              startIcon={<FileUploadOutlinedIcon />}
              onClick={onUploadClick}
              size="small"
            >
              Upload
            </Button>
            <SubmitJobButton
              job={job}
              size="small"
              onSuccess={onSubmitSuccess}
              onError={onSubmitError}
            />
          </Stack>
        )}
        {context === "ops" && (() => {
          // Check if this is an Ultratax firm
          const taxSoftwareName = (job.taxSoftwareName || "").toLowerCase().replace(/\s+/g, "");
          const isUltraTax = taxSoftwareName.includes("ultratax");

          // Determine if we should show the processing button
          const showProcessingButton =
            job.status === "TRIAGE" ||
            job.status === "NEW_FILES" ||
            job.status === "DOWNLOAD_TO_PROCESS";

          // Determine button text and icon based on firm type
          const isDownloadWorkflow = isUltraTax || job.status === "DOWNLOAD_TO_PROCESS";
          const buttonText = isDownloadWorkflow ? "Download to Process" : "Start Processing";
          const loadingText = isDownloadWorkflow ? "Preparing..." : "Starting...";
          const buttonIcon = isDownloadWorkflow
            ? <FileUploadOutlinedIcon sx={{ transform: 'rotate(180deg)' }} />
            : <SendIcon />;

          if (!showProcessingButton && !showDownloadAll) return null;

          return (
            <Stack direction="row" spacing={1}>
              {showDownloadAll && (
                <Button
                  variant="text"
                  size="small"
                  startIcon={
                    downloadingAll ? (
                      <CircularProgress size={16} />
                    ) : (
                      <FileDownloadOutlinedIcon />
                    )
                  }
                  onClick={handleDownloadAll}
                  disabled={downloadingAll}
                  sx={{ px: 2, fontWeight: 700 }}
                >
                  Download All
                </Button>
              )}
              {showProcessingButton && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  startIcon={buttonIcon}
                  onClick={onStartProcessing}
                  disabled={isStartProcessingPending}
                >
                  {isStartProcessingPending ? loadingText : buttonText}
                </Button>
              )}
            </Stack>
          );
        })()}
      </Box>

      <Collapse in={open}>
        <UploadedFilesList
          files={displayFiles}
          job={job}
          onDownload={onFileDownload}
          onPreview={(file, _index) =>
            onFilePreview(
              file,
              displayFiles.findIndex((f) => f.id === file.id),
              displayFiles,
            )
          }
          onDelete={onFileDelete}
          onUpdateFileType={context === "ops" ? undefined : onUpdateFileType}
          onUpdateBucket={context === "ops" ? onUpdateBucket : undefined}
          showActions={true}
          compact={false}
          allowDelete={true}
          enableViewer={false} // Using custom preview handler
          groupIntoBuckets={context !== "ops"} // Don't group for ops context
        />
      </Collapse>

      {notification.NotificationComponent}
    </Box>
  );
}
